import moment from 'moment';
import 'moment/locale/ru';
import 'moment/locale/es';
import 'moment/locale/uk';
import jwt_decode from 'jwt-decode';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { isMobileDevice, LocalStorageService, parseJwt } from '@/helper';
import { useLocalStorage } from '@/src/hooks/useLocalStorage';
import { ReactGA, initGA, trackingId } from '@/src/utils/analytics';

import Notification from '../common/Notification';
import { useContextDispatch, useContextState } from '../context';
import DownloadNotificationBanner from '../DownloadNotificationBanner/DownloadNotificationBanner';
import LicenseExpiredBanner from '../LicenseExpiredBanner/LicenseExpiredBanner';
import { updateToken } from './service';
import { usePersonalLicenses } from '@/src/hooks/usePersonalLicenses';

const CookieBanner = dynamic(() => import('../CookieBanner/CookieBanner'));
const Header = dynamic(() => import('../Header/Header'));
const Footer = dynamic(() => import('../Footer/Footer'));
const HeadComponent = dynamic(() => import('../Head'));
const SurveyPopup = dynamic(() => import('../SurveyPopup/SurveyPopup'));

interface AppProps {
  children?: React.ReactNode;
}

const App: React.FC<AppProps> = ({ children }) => {
  const { authorizedAction, userAction, isMobileDeviceAction } = useContextDispatch();
  const { authorized } = useContextState();
  const { push, pathname, locale, asPath, query } = useRouter();
  const { set } = useLocalStorage();
  const { getPersonalLicenses } = usePersonalLicenses();

  useEffect(() => {
    moment.locale(locale === 'ua' ? 'uk' : locale);
  }, [locale]);

  useEffect(() => {
    if (asPath !== '/terminal/login') {
      set('previousPage', asPath);
    }
    if (query.clickid !== undefined) {
      set('clickId', query?.clickid);
    }
    if (query.pid) {
      LocalStorageService.remove('referral_user_id');
      LocalStorageService.set('aff_id', query?.pid);
    }
  }, [query, set]);

  useEffect(() => {
    const savedUtm = sessionStorage.getItem('utm');
    const isUtm = /utm_source/i.test(asPath);

    if (isUtm || savedUtm) {
      const utmIndex = asPath.indexOf('utm_source');
      let utmPath = asPath.slice(utmIndex);
      if (utmIndex === -1 && savedUtm) {
        utmPath = savedUtm;
      }
      sessionStorage.setItem('utm', utmPath);
      window.history.replaceState(null, '', `?${utmPath}`);
      return;
    }
    sessionStorage.setItem('utm', '');

    if (typeof window !== undefined) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    }
  }, [asPath, locale, pathname]);

  useEffect(() => {
    if (authorized) {
      getPersonalLicenses();
    }
  }, [authorized, locale]);

  useEffect(() => {
    const refreshToken = LocalStorageService.get('refreshToken');

    if (refreshToken && !authorized) {
      updateToken({ refreshToken });
      authorizedAction(true);
    }

    if (authorized) {
      const { email, username } = parseJwt();
      userAction({
        name: username || email,
        email,
      });
    }
  }, [authorized, authorizedAction, userAction, locale, pathname]);

  useEffect(() => {
    const value = isMobileDevice();
    isMobileDeviceAction(value);
  }, [isMobileDeviceAction]);

  useEffect(() => {
    const accessToken = LocalStorageService.get('accessToken');
    if (authorized && !!accessToken) {
      const token = jwt_decode(accessToken) as any;
      ReactGA.set({
        userId: token.userId,
      });
    }
  }, [authorized]);

  useEffect(() => {
    initGA(trackingId);
  }, []);

  return (
    <>
      <HeadComponent />
      <Notification />
      <Header />
      <CookieBanner />
      <LicenseExpiredBanner />
      <DownloadNotificationBanner />
      {children}
      <SurveyPopup isEnabled={authorized ?? false} />
      <Footer />
      <div className="page-fader" />
      <div id="portal-root" />
    </>
  );
};

export default App;
